import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useCart } from '../contexts/CartContext'
import WishlistButton from '../components/WishlistButton'

interface Product {
  id: string
  name: string
  description: string
  price: number
  stock: number
  category: string
  image_urls: string[]
  created_at: string
}

type SortOption = 'newest' | 'price-asc' | 'price-desc' | 'name-asc'

export default function Products() {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [addingToCart, setAddingToCart] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string; productId?: string } | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const { addToCart } = useCart()

  // Extract unique categories from products
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(products.map(p => p.category))].filter(Boolean)
    return uniqueCategories.sort()
  }, [products])

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    // First, filter products
    const filtered = products.filter(product => {
      // Category filter
      if (selectedCategory && product.category !== selectedCategory) {
        return false
      }

      // Price range filter
      const min = minPrice ? parseFloat(minPrice) : null
      const max = maxPrice ? parseFloat(maxPrice) : null

      if (min !== null && !isNaN(min) && product.price < min) {
        return false
      }
      if (max !== null && !isNaN(max) && product.price > max) {
        return false
      }

      // Search filter
      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      return (
        product.name.toLowerCase().includes(query) ||
        product.description.toLowerCase().includes(query) ||
        product.category.toLowerCase().includes(query)
      )
    })

    // Then, sort the filtered results
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'price-asc':
          return a.price - b.price
        case 'price-desc':
          return b.price - a.price
        case 'name-asc':
          return a.name.localeCompare(b.name)
        case 'newest':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      }
    })

    return sorted
  }, [products, searchQuery, selectedCategory, minPrice, maxPrice, sortBy])

  // Check if any price filter is active
  const hasPriceFilter = minPrice !== '' || maxPrice !== ''

  // Check if any filter is active (for showing "Clear All" button)
  const hasAnyFilter = searchQuery !== '' ||
                       selectedCategory !== null ||
                       hasPriceFilter ||
                       sortBy !== 'newest'

  // Clear all filters function
  const clearAllFilters = () => {
    setSearchQuery('')
    setSelectedCategory(null)
    setMinPrice('')
    setMaxPrice('')
    setSortBy('newest')
  }

  useEffect(() => {
    fetchProducts()
  }, [])

  const fetchProducts = async () => {
    try {
      setLoading(true)

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

      const response = await fetch(`${supabaseUrl}/rest/v1/products?select=*&order=created_at.desc`, {
        method: 'GET',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(10000)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      setProducts(data || [])
    } catch (error) {
      console.error('Error fetching products:', error)
      setProducts([])
    } finally {
      setLoading(false)
    }
  }

  const handleAddToCart = async (e: React.MouseEvent, product: Product) => {
    e.preventDefault() // Prevent navigating to product detail
    e.stopPropagation()

    setAddingToCart(product.id)
    setMessage(null)

    // Pass product data for guest cart support
    const productData = {
      id: product.id,
      name: product.name,
      price: product.price,
      image_urls: product.image_urls,
      stock: product.stock
    }

    const result = await addToCart(product.id, null, 1, productData)

    if (result.success) {
      setMessage({ type: 'success', text: 'Added to cart!', productId: product.id })
      setTimeout(() => setMessage(null), 2000)
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to add', productId: product.id })
      setTimeout(() => setMessage(null), 3000)
    }

    setAddingToCart(null)
  }

  const getStockBadge = (stock: number) => {
    if (stock === 0) {
      return <span className="badge-sold-out">Sold Out</span>
    } else if (stock <= 5) {
      return <span className="badge-low-stock">Low Stock</span>
    }
    return <span className="badge-in-stock">In Stock</span>
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Product List</h1>
          <p className="text-gray-500 text-sm">Browse our collection of quality products</p>
        </div>

        {/* Search Bar and Sort */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search Input */}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5 text-gray-400"
                  style={{ width: '20px', height: '20px' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search products by name, description, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-5 h-5"
                    style={{ width: '20px', height: '20px' }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="relative sm:w-48">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="w-full appearance-none px-4 py-3 pr-10 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all shadow-sm cursor-pointer"
              >
                <option value="newest">Newest First</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-4 h-4 text-gray-400"
                  style={{ width: '16px', height: '16px' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                </svg>
              </div>
            </div>
          </div>

          {/* Search results count and Clear All Filters */}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-gray-500">
              {searchQuery && (
                <span>{filteredProducts.length} {filteredProducts.length === 1 ? 'result' : 'results'} for "{searchQuery}"</span>
              )}
            </div>

            {/* Clear All Filters Button - only show when filters are active */}
            {hasAnyFilter && (
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-4 h-4"
                  style={{ width: '16px', height: '16px' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        {/* Category Filter */}
        {!loading && categories.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {/* All Products Button */}
              <button
                onClick={() => setSelectedCategory(null)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === null
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                All Products
              </button>

              {/* Category Buttons */}
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Active filter indicator */}
            {selectedCategory && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} in "{selectedCategory}"
                </span>
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  Clear filter
                </button>
              </div>
            )}
          </div>
        )}

        {/* Price Range Filter */}
        {!loading && (
          <div className="mb-6">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[140px] max-w-[200px]">
                <label htmlFor="minPrice" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Min Price
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 pointer-events-none">
                    $
                  </span>
                  <input
                    type="number"
                    id="minPrice"
                    placeholder="0"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    min="0"
                    step="0.01"
                    className="w-full pl-7 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div className="flex items-center pb-2.5">
                <span className="text-gray-400">—</span>
              </div>

              <div className="flex-1 min-w-[140px] max-w-[200px]">
                <label htmlFor="maxPrice" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Max Price
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 pointer-events-none">
                    $
                  </span>
                  <input
                    type="number"
                    id="maxPrice"
                    placeholder="Any"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    min="0"
                    step="0.01"
                    className="w-full pl-7 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* Clear price filter button */}
              {hasPriceFilter && (
                <button
                  onClick={() => {
                    setMinPrice('')
                    setMaxPrice('')
                  }}
                  className="pb-2.5 text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-4 h-4"
                    style={{ width: '16px', height: '16px' }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear
                </button>
              )}
            </div>

            {/* Price filter indicator */}
            {hasPriceFilter && (
              <p className="mt-2 text-sm text-gray-500">
                Showing products
                {minPrice && ` from $${parseFloat(minPrice).toFixed(2)}`}
                {minPrice && maxPrice && ' to'}
                {maxPrice && ` up to $${parseFloat(maxPrice).toFixed(2)}`}
              </p>
            )}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-8 h-8 text-gray-400"
              >
                {searchQuery ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                ) : selectedCategory ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
                  />
                ) : hasPriceFilter ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                  />
                )}
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery || selectedCategory || hasPriceFilter ? 'No products found' : 'No products available'}
            </h3>
            <p className="text-gray-500 text-sm">
              {searchQuery || selectedCategory || hasPriceFilter ? (
                <span className="flex flex-col items-center gap-2">
                  <span>Try adjusting your filters</span>
                  <span className="flex flex-wrap justify-center gap-2">
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="text-purple-600 hover:text-purple-700 font-medium">
                        Clear search
                      </button>
                    )}
                    {searchQuery && (selectedCategory || hasPriceFilter) && <span className="text-gray-300">|</span>}
                    {selectedCategory && (
                      <button onClick={() => setSelectedCategory(null)} className="text-purple-600 hover:text-purple-700 font-medium">
                        Clear category
                      </button>
                    )}
                    {selectedCategory && hasPriceFilter && <span className="text-gray-300">|</span>}
                    {hasPriceFilter && (
                      <button onClick={() => { setMinPrice(''); setMaxPrice(''); }} className="text-purple-600 hover:text-purple-700 font-medium">
                        Clear price
                      </button>
                    )}
                  </span>
                </span>
              ) : (
                'Check back soon for new items!'
              )}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="group bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md hover:border-gray-300"
              >
                {/* Product Image - Clickable to Detail */}
                <div className="relative h-36 bg-gray-50 flex items-center justify-center p-3 border-b border-gray-100 overflow-hidden">
                  {/* Wishlist Button */}
                  <div className="absolute top-2 right-2 z-10">
                    <WishlistButton productId={product.id} size="sm" />
                  </div>
                  <Link to={`/products/${product.id}`} className="flex items-center justify-center w-full h-full">
                    {product.image_urls && product.image_urls.length > 0 ? (
                      <img
                        src={product.image_urls[0]}
                        alt={product.name}
                        className="img-product-grid group-hover:scale-105 transition-transform duration-300"
                        style={{ width: '120px', height: '120px', maxWidth: '120px', maxHeight: '120px' }}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1}
                          stroke="currentColor"
                          className="w-8 h-8 text-gray-300"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                          />
                        </svg>
                      </div>
                    )}
                  </Link>
                </div>

                {/* Product Details */}
                <div className="p-4">
                  {/* Product Name - Clickable to Detail */}
                  <Link to={`/products/${product.id}`}>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-1 hover:text-purple-600 transition-colors" title={product.name}>
                      {product.name}
                    </h3>
                  </Link>

                  {/* Category */}
                  <p className="text-xs text-gray-400 mb-3">
                    {product.category}
                  </p>

                  {/* Price and Stock */}
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-lg font-bold text-gray-900">
                      ${product.price.toFixed(2)}
                    </p>
                    {getStockBadge(product.stock)}
                  </div>

                  {/* Add to Cart Message */}
                  {message && message.productId === product.id && (
                    <div className={`mb-2 p-2 rounded-lg text-xs text-center ${
                      message.type === 'success'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-700'
                    }`}>
                      {message.text}
                    </div>
                  )}

                  {/* Add to Cart Button */}
                  <button
                    onClick={(e) => handleAddToCart(e, product)}
                    disabled={product.stock === 0 || addingToCart === product.id}
                    className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      product.stock > 0 && addingToCart !== product.id
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {addingToCart === product.id ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Adding...
                      </>
                    ) : product.stock > 0 ? (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="w-4 h-4"
                          style={{ width: '16px', height: '16px' }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4.5v15m7.5-7.5h-15"
                          />
                        </svg>
                        Add to Cart
                      </>
                    ) : (
                      'Out of Stock'
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
